import { Button, Drawer, Flex, Input, Radio, Select, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import type { ChartVisualisation } from "../../../../components/widgets/types.ts"
import { filterByLabelOrValue } from "../../../DashboardPage/components/Portfolio/FilterBar.tsx"
import type { CatalogWidgetSpec } from "../../../DashboardPage/redux/widgets.types.ts"
import { createEmptyReportWidgetFilters } from "../../helpers/reportWidgetFilters.ts"
import { useReportFilterOptions } from "../../hooks/useReportFilterOptions.ts"
import {
  REPORT_COMPARE_OVERRIDES,
  type ReportCompareOverride,
  type ReportFilterDimension,
  WIDGET_SIZE_PRESETS
} from "../../types/canvas.types.ts"
import type {
  ReportWidgetEntry,
  ReportWidgetFilters,
  WidgetSizePreset
} from "../../types/canvas.types.ts"

const { Text } = Typography

const COMPARE_OPTION_LABEL_KEYS: Record<ReportCompareOverride, string> = {
  inherit: "reports.widgetConfig.compareOptions.inherit",
  none: "reports.widgetConfig.compareOptions.none",
  previous_period: "reports.widgetConfig.compareOptions.previousPeriod",
  same_period_last_year:
    "reports.widgetConfig.compareOptions.samePeriodLastYear"
}

const FILTER_LABEL_KEYS: Record<ReportFilterDimension, string> = {
  project: "reports.widgetConfig.filters.project",
  pm: "reports.widgetConfig.filters.pm",
  client: "reports.widgetConfig.filters.client",
  project_type: "reports.widgetConfig.filters.projectType",
  project_status: "reports.widgetConfig.filters.projectStatus",
  status: "reports.widgetConfig.filters.status",
  state: "reports.widgetConfig.filters.state"
}

const FILTER_DIMENSION_ORDER: ReportFilterDimension[] = [
  "project",
  "pm",
  "client",
  "project_type",
  "project_status",
  "status",
  "state"
]

interface ReportWidgetConfigDrawerProps {
  open: boolean
  widget: ReportWidgetEntry | null
  spec: CatalogWidgetSpec | undefined
  visualisationOptions: ChartVisualisation[] | null
  onClose: () => void
  onChangeTitle: (id: string, title: string | undefined) => void
  onChangeSize: (id: string, size: WidgetSizePreset) => void
  onChangeFilters: (
    id: string,
    filters: ReportWidgetFilters | undefined
  ) => void
  onChangeCompare: (id: string, compare: ReportCompareOverride) => void
  onChangeVisualisation: (
    id: string,
    visualisation: ChartVisualisation | undefined
  ) => void
}

export const ReportWidgetConfigDrawer = ({
  open,
  widget,
  spec,
  visualisationOptions,
  onClose,
  onChangeTitle,
  onChangeSize,
  onChangeFilters,
  onChangeCompare,
  onChangeVisualisation
}: ReportWidgetConfigDrawerProps) => {
  const { t } = useTranslation()
  const { options, loading } = useReportFilterOptions()
  const [titleDraft, setTitleDraft] = useState("")

  useEffect(() => {
    setTitleDraft(widget?.title ?? "")
  }, [widget?.id, widget?.title])

  if (!widget) return null

  const filters = widget.filters ?? createEmptyReportWidgetFilters()

  const handleTitleBlur = () => {
    if (titleDraft.trim() === (widget.title ?? "")) return
    onChangeTitle(widget.id, titleDraft)
  }

  const handleFilterChange =
    (dimension: ReportFilterDimension) => (value: string[]) => {
      onChangeFilters(widget.id, { ...filters, [dimension]: value })
    }

  const handleClearFilters = () => onChangeFilters(widget.id, undefined)

  return (
    <Drawer
      title={t("reports.widgetConfig.title")}
      open={open}
      onClose={onClose}
      width={420}
      destroyOnHidden
    >
      <Flex vertical gap="large">
        <Flex vertical gap="small">
          <Text strong>{t("reports.widgetConfig.fields.title")}</Text>
          <Input
            value={titleDraft}
            placeholder={t("reports.widgetConfig.fields.titlePlaceholder", {
              fallback: spec?.title ?? ""
            })}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={handleTitleBlur}
          />
        </Flex>

        <Flex vertical gap="small">
          <Text strong>{t("reports.widgetConfig.fields.size")}</Text>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            value={widget.size}
            onChange={(e) =>
              onChangeSize(widget.id, e.target.value as WidgetSizePreset)
            }
            options={WIDGET_SIZE_PRESETS.map((preset) => ({
              label: t(`reports.widgetConfig.sizeOptions.${preset}`),
              value: preset
            }))}
          />
        </Flex>

        {visualisationOptions && (
          <Flex vertical gap="small">
            <Text strong>
              {t("reports.widgetConfig.fields.visualisation")}
            </Text>
            <Select<ChartVisualisation>
              allowClear
              value={widget.visualisation}
              placeholder={t(
                "reports.widgetConfig.visualisationOptions.default"
              )}
              onChange={(value) =>
                onChangeVisualisation(widget.id, value ?? undefined)
              }
              options={visualisationOptions.map((option) => ({
                label: t(`reports.widgetConfig.visualisationOptions.${option}`),
                value: option
              }))}
            />
          </Flex>
        )}

        <Flex vertical gap="small">
          <Text strong>{t("reports.widgetConfig.fields.compare")}</Text>
          <Select<ReportCompareOverride>
            value={widget.compare ?? "inherit"}
            onChange={(value) => onChangeCompare(widget.id, value)}
            options={REPORT_COMPARE_OVERRIDES.map((option) => ({
              label: t(COMPARE_OPTION_LABEL_KEYS[option]),
              value: option
            }))}
          />
        </Flex>

        <Flex vertical gap="small">
          <Flex justify="space-between" align="center">
            <Text strong>{t("reports.widgetConfig.filters.title")}</Text>
            <Button size="small" onClick={handleClearFilters}>
              {t("reports.widgetConfig.filters.clear")}
            </Button>
          </Flex>
          {FILTER_DIMENSION_ORDER.map((dimension) => (
            <Select<string[]>
              key={dimension}
              mode="multiple"
              allowClear
              showSearch
              loading={loading}
              maxTagCount="responsive"
              filterOption={filterByLabelOrValue}
              placeholder={t(FILTER_LABEL_KEYS[dimension])}
              value={filters[dimension] ?? []}
              options={options[dimension]}
              onChange={handleFilterChange(dimension)}
            />
          ))}
        </Flex>
      </Flex>
    </Drawer>
  )
}
